import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GripVertical, Mail, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { cn } from '@/lib/utils';
import { RecruitmentTabs } from '../components/RecruitmentTabs';
import { CANDIDATE_STATUSES, type CandidateStatus } from '../constants';
import {
  useCandidates,
  useDeleteCandidate,
  useOpenings,
  useUpdateCandidate,
} from '../queries';
import type { Candidate } from '../types';
import { candidateName } from '../types';

const COLUMN_TINT: Record<CandidateStatus, string> = {
  new: 'border-t-slate-400',
  screening: 'border-t-blue-500',
  interviewing: 'border-t-violet-500',
  offer: 'border-t-amber-500',
  hired: 'border-t-emerald-600',
  rejected: 'border-t-rose-600',
};

export function CandidatesPage() {
  const navigate = useNavigate();
  const { data, isPending, isError, error, isFetching } = useCandidates();
  const { data: openingsData } = useOpenings();
  const update = useUpdateCandidate();
  const remove = useDeleteCandidate();

  const [toDelete, setToDelete] = useState<Candidate | null>(null);
  const [dropTarget, setDropTarget] = useState<CandidateStatus | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const openings = useMemo(() => openingsData?.results ?? [], [openingsData]);
  const openingLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of openings) map.set(o.id, o.title);
    return map;
  }, [openings]);

  const candidates = useMemo(() => data?.results ?? [], [data]);
  const buckets = useMemo(() => {
    const map = new Map<CandidateStatus, Candidate[]>();
    for (const s of CANDIDATE_STATUSES) map.set(s.value, []);
    for (const c of candidates) map.get(c.status)?.push(c);
    return map;
  }, [candidates]);

  function handleDrop(id: number, target: CandidateStatus) {
    const current = candidates.find((c) => c.id === id);
    if (!current || current.status === target) return;
    update.mutate({ id, input: { status: target } });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recruitment</h1>
          <p className="text-muted-foreground text-sm">Candidates by stage — drag to update.</p>
        </div>
        <Button asChild>
          <Link to="/hrm/recruitment/candidates/new">
            <Plus className="size-4" /> New candidate
          </Link>
        </Button>
      </div>

      <RecruitmentTabs />

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load candidates.'}
        </p>
      ) : isPending ? (
        <div className="flex gap-4 overflow-x-auto pb-2" aria-busy={isFetching}>
          {CANDIDATE_STATUSES.map((s) => (
            <Skeleton key={s.value} className="h-72 w-72 shrink-0 rounded-md" />
          ))}
        </div>
      ) : (
        <div
          className="flex gap-4 overflow-x-auto pb-2"
          onDragEnd={() => {
            setDraggingId(null);
            setDropTarget(null);
          }}
        >
          {CANDIDATE_STATUSES.map((s) => {
            const list = buckets.get(s.value) ?? [];
            return (
              <div
                key={s.value}
                className="flex w-72 shrink-0 flex-col"
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dropTarget !== s.value) setDropTarget(s.value);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = Number(e.dataTransfer.getData('text/plain'));
                  if (Number.isFinite(id)) handleDrop(id, s.value);
                  setDropTarget(null);
                }}
              >
                <div className={cn('rounded-t-md border-t-4 bg-muted/40 p-3', COLUMN_TINT[s.value])}>
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold">{s.label}</h2>
                    <Badge variant="outline" className="tabular-nums">{list.length}</Badge>
                  </div>
                </div>
                <div
                  className={cn(
                    'bg-muted/20 flex min-h-[200px] flex-1 flex-col gap-2 rounded-b-md p-2 transition-colors',
                    dropTarget === s.value && draggingId != null && 'bg-primary/10 ring-primary/30 ring-2 ring-inset',
                  )}
                >
                  {list.length === 0 ? (
                    <p className="text-muted-foreground py-6 text-center text-xs">No candidates</p>
                  ) : (
                    list.map((c) => (
                      <div
                        key={c.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', String(c.id));
                          e.dataTransfer.effectAllowed = 'move';
                          setDraggingId(c.id);
                        }}
                        className="bg-card hover:border-primary group flex cursor-grab items-start gap-2 rounded-md border p-3 shadow-sm active:cursor-grabbing"
                      >
                        <GripVertical className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                        <div
                          role="button"
                          tabIndex={0}
                          className="min-w-0 flex-1 cursor-pointer"
                          onClick={() => navigate(`/hrm/recruitment/candidates/${c.public_id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/hrm/recruitment/candidates/${c.public_id}`);
                            }
                          }}
                        >
                          <p className="truncate text-sm font-medium">{candidateName(c)}</p>
                          <p className="text-muted-foreground flex items-center gap-1 text-xs">
                            {c.email ? (
                              <>
                                <Mail className="size-3" />
                                <span className="truncate">{c.email}</span>
                              </>
                            ) : null}
                          </p>
                          {c.job_opening ? (
                            <Badge
                              variant="outline"
                              className="mt-1 block max-w-full truncate text-[10px]"
                              title={openingLabel.get(c.job_opening) ?? `Opening #${c.job_opening}`}
                            >
                              {openingLabel.get(c.job_opening) ?? `Opening #${c.job_opening}`}
                            </Badge>
                          ) : null}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete candidate"
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                          onClick={() => setToDelete(c)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `${candidateName(toDelete)} will be removed.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
